/**
 * Hook for managing sidebar favorites
 * localStorage for instant reads, Supabase profiles.ui_preferences for persistence
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY_PREFIX = 'trainerpulse_sidebar_favorites_';

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function readLocal(userId: string): string[] {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return [];
}

function writeLocal(userId: string, favorites: string[]) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(favorites));
}

/** Clear sidebar favorites for a specific user (for logout cleanup) */
export function clearSidebarFavorites(userId: string) {
  localStorage.removeItem(getStorageKey(userId));
}

export function useSidebarFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  // On mount: read localStorage instantly, then sync from Supabase
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      setUserId(user.id);

      // Instant: read localStorage
      const local = readLocal(user.id);
      setFavorites(local);

      // Then sync from Supabase
      const { data: profile } = await supabase
        .from('profiles')
        .select('ui_preferences')
        .eq('id', user.id)
        .single();

      if (cancelled) return;

      const prefs = (profile?.ui_preferences as Record<string, unknown>) ?? {};
      const remote = Array.isArray(prefs.sidebar_favorites)
        ? (prefs.sidebar_favorites as string[])
        : [];

      // Remote wins (it's the source of truth across devices)
      setFavorites(remote);
      writeLocal(user.id, remote);
    };

    init();
    return () => { cancelled = true; };
  }, []);

  const favoritesSet = useMemo(() => new Set(favorites), [favorites]);

  const isFavorite = useCallback(
    (itemId: string) => favoritesSet.has(itemId),
    [favoritesSet],
  );

  const toggleFavorite = useCallback(
    async (itemId: string) => {
      if (!userId) return;

      // Optimistic local update
      const next = favoritesSet.has(itemId)
        ? favorites.filter((id) => id !== itemId)
        : [...favorites, itemId];

      setFavorites(next);
      writeLocal(userId, next);

      // Persist to Supabase — merge into existing ui_preferences
      const { data: profile } = await supabase
        .from('profiles')
        .select('ui_preferences')
        .eq('id', userId)
        .single();

      const existing = (profile?.ui_preferences as Record<string, unknown>) ?? {};

      await supabase
        .from('profiles')
        .update({
          ui_preferences: { ...existing, sidebar_favorites: next },
        })
        .eq('id', userId);
    },
    [userId, favorites, favoritesSet],
  );

  return { favorites, isFavorite, toggleFavorite };
}
